import { useEffect, useState } from 'react';
import { Grid, TextField } from '@mui/material';
import IconWarning from 'src/assets/images/icons/warning-2-red.svg';
import IconClose from 'src/assets/images/icons/close-square.svg';
import { camelCaseToWords } from 'src/utils/utility';
import { InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';
import { TabsProps } from '../FormValidateInvoice';

export const InvoiceDetailsTab = ({
  localJson,
  setLocalJson,
  isValidJson,
  setIsValidJson,
  validateJson,
  validateUpdatedJson,
  ocrMessage,
  activeStep,
  viewMode,
}: TabsProps) => {
  const [rawJsonInvoice, setRawJsonInvoice] = useState<string>();
  useEffect(() => {
    const { shipments, serviceProviderAddresses, billedPartyAddresses, bankDetails, ...invoiceDetailsOnly } = localJson || {};
    setRawJsonInvoice(JSON.stringify(invoiceDetailsOnly, null, 2));
  }, [localJson]);

  const handleRawJsonInvoiceChange = (text: string) => {
    setRawJsonInvoice(text);
    try {
      JSON.parse(text);
      setIsValidJson(true);
    } catch {
      setIsValidJson(false);
    }
  };

  const handleRawJsonInvoiceBlur = () => {
    try {
      const parsed = JSON.parse(rawJsonInvoice);
      setIsValidJson(true);
      setLocalJson({ ...localJson, ...parsed });
    } catch {
      setIsValidJson(false);
    }
  };

  const getHelperText = (
    fieldName: keyof InvoiceMessageDto,
    validateJson: any,
    localJson: InvoiceMessageDto,
    ocrMessage: InvoiceMessageDto,
    activeStep: ValidateInvoiceStep
  ) => {
    if (
      activeStep === ValidateInvoiceStep.Step1_EditOCRData &&
      !!validateJson?.[fieldName] &&
      validateJson[fieldName].isValid === false &&
      validateJson?.[fieldName]?.exception?.error
    ) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
          <img src={IconWarning} alt='warning icon' style={{ height: 12, marginRight: 4 }} />
          {validateJson?.[fieldName]?.exception?.error}
        </span>
      );
    } else if (activeStep === ValidateInvoiceStep.Step2_ReviewData && localJson?.[fieldName] !== ocrMessage?.[fieldName]) {
      return (
        <span style={{ display: 'flex', alignItems: 'center', color: !validateJson?.[fieldName]?.isValid ? '#E72547' : '#9F9F9F', fontSize: 10 }}>
          <img src={!validateJson?.[fieldName]?.isValid ? IconWarning : IconClose} alt='warning icon' style={{ height: 12, marginRight: 4 }} />
          {ocrMessage?.[fieldName] as string}
        </span>
      );
    }
  };

  if (viewMode === 'structured') {
    const fieldEntries = Object.entries(JSON.parse(rawJsonInvoice || '{}') || {});
    const rows = [];
    for (let i = 0; i < fieldEntries.length; i += 3) {
      rows.push(fieldEntries.slice(i, i + 3));
    }
    return (
      <div className='space-y-4'>
        {rows.map((row, rowIdx) => (
          <Grid container spacing={2} key={rowIdx}>
            {row.map(([field, value]) => (
              <Grid item xs={12} md={4} key={field}>
                <TextField
                  fullWidth
                  label={camelCaseToWords(field)}
                  value={value}
                  onChange={(e) => setRawJsonInvoice(JSON.stringify({ ...JSON.parse(rawJsonInvoice), [field]: e.target.value }))}
                  onBlur={() => setLocalJson({ ...localJson, ...JSON.parse(rawJsonInvoice) })}
                  error={validateJson?.[field]?.isValid === false && activeStep === ValidateInvoiceStep.Step1_EditOCRData}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                    },
                    ...(validateUpdatedJson?.[field]?.isValid === false && {
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#E72547',
                        },
                      },
                    }),
                    ...(validateJson?.[field]?.isValid === false &&
                      validateUpdatedJson?.[field]?.isValid === true && {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#4CAF50',
                          },
                        },
                      }),
                  }}
                  helperText={getHelperText(field as keyof InvoiceMessageDto, validateJson, localJson, ocrMessage, activeStep)}
                  InputProps={{ readOnly: activeStep !== ValidateInvoiceStep.Step1_EditOCRData }}
                  size='small'
                />
              </Grid>
            ))}
          </Grid>
        ))}
      </div>
    );
  } else {
    return (
      <TextField
        multiline
        fullWidth
        rows={20}
        label='Invoice Data'
        value={rawJsonInvoice}
        onChange={(e) => handleRawJsonInvoiceChange(e.target.value)}
        error={!isValidJson}
        helperText={!isValidJson ? 'Invalid JSON' : ''}
        onBlur={handleRawJsonInvoiceBlur}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
          '& .MuiInputLabel-root': {
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: '12px',
          },
        }}
        disabled={activeStep !== ValidateInvoiceStep.Step1_EditOCRData}
      />
    );
  }
};
